"use client"

import { ChevronDown, Settings, Building2, Briefcase, LogOut, Moon, Sun, Monitor, Zap } from "lucide-react"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { PATH_AUTH } from "@/routes/paths"
import { useTheme } from "src/contexts/ThemeContext"
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/menu"
import { useAuth } from "src/hooks/auth/useAuth"
import { PATH_ROOT } from "src/routes/paths"
import { Avatar } from "@/components/ui/Avatar"

type User = {
  name: string
  email: string
  profileImageUrl?: string
}

type NavUserProps = {
  user: User
  isDemo?: boolean
}

const themeOptions = [
  {
    label: 'Light',
    value: 'light',
    icon: <Sun className="size-4" />
  },
  {
    label: 'Dark',
    value: 'dark',
    icon: <Moon className="size-4" />
  },
  {
    label: 'System',
    value: 'system',
    icon: <Monitor className="size-4" />
  }
]

export function NavUser({ user, isDemo = false }: NavUserProps) {
  const [mounted, setMounted] = useState(false)
  const navigate = useNavigate()
  const { logout } = useAuth()
  const { theme, setTheme } = useTheme()

  useEffect(() => {
    setMounted(true)
  }, [])

  const handleLogout = () => {
    if (isDemo) {
      navigate(PATH_AUTH.signIn)
    } else {
      logout()
    }
  }

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger render={(
            <SidebarMenuButton
              size="lg"
              className="group/user h-auto min-h-11 data-[popup-open]:bg-sidebar-accent data-[popup-open]:text-sidebar-accent-foreground"
            >
              <div className="shrink-0">
                <Avatar
                  src={user.profileImageUrl}
                  alt={user.name || "Profile"}
                  className="size-7"
                />
              </div>
              <div className="grid flex-1 text-left text-sm leading-tight">
                <span className="truncate font-semibold">{user.name || 'Mayowa Arogundade'}</span>
                <span className="truncate text-xs text-muted-foreground">{user.email || 'mayowa@equal-iq.com'}</span>
              </div>
              <ChevronDown className="ml-auto size-4 transition-transform duration-200 group-data-[popup-open]/user:rotate-180" />
            </SidebarMenuButton>
          )} />
          <DropdownMenuContent
            className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg z-9999"
            side="bottom"
            align="start"
            sideOffset={4}
          >
            <DropdownMenuItem
              className="w-full"
              onClick={() => {
                if (isDemo) {
                  navigate(PATH_ROOT.demo.profile)
                } else {
                  navigate(PATH_ROOT.profile)
                }
              }}
            >
              <Settings />
              <span>My Profile</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                if (isDemo) {
                  navigate(PATH_ROOT.demo.organizations)
                } else {
                  navigate(PATH_ROOT.organization.organizations)
                }
              }}
            >
              <Building2 />
              <span>My Organizations</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                if (isDemo) {
                  navigate(PATH_ROOT.demo.rules)
                } else {
                  navigate(PATH_ROOT.main.rules)
                }
              }}
            >
              <Zap />
              <span>Rules</span>
            </DropdownMenuItem>
            {isDemo && (
              <DropdownMenuItem
                onClick={() => {
                  navigate(PATH_ROOT.demo.selector)
                }}
              >
                <Briefcase />
                <span>Switch Workspace</span>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => {
                navigate(isDemo ? PATH_ROOT.demo.settings : PATH_ROOT.settings.root)
              }}
            >
              <Settings />
              <span>Settings</span>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {mounted && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                  {theme === 'dark' ? <Moon className="size-4" /> : theme === 'light' ? <Sun className="size-4" /> : <Monitor className="size-4" />}
                  <span>Theme</span>
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as 'light' | 'dark' | 'system')}>
                    {themeOptions.map((option) => (
                      <DropdownMenuRadioItem key={option.value} value={option.value}>
                        <div className="flex items-center gap-2">
                          {option.icon}
                          {option.label}
                        </div>
                      </DropdownMenuRadioItem>
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              variant="destructive"
              onClick={handleLogout}
            >
              <LogOut />
              <span>{isDemo ? 'Exit Demo' : 'Sign Out'}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}

